import { useCallback, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Receipt, Search, Trash2 } from "lucide-react";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { DatePickerField } from "@/components/shared/DatePickerField";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { TablePagination } from "@/components/shared/TablePagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import { expenseKeys } from "@/lib/queryKeys";
import {
  deleteExpense,
  listExpenses,
  type Expense,
} from "@/services/expensesService";

const SEARCH_DEBOUNCE_MS = 300;

function formatMoney(amount: string | null, currency: string): string {
  const n = parseFloat(amount ?? "0");
  if (!Number.isFinite(n)) return "—";
  const formatted = new Intl.NumberFormat("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
  return `${currency} ${formatted}`;
}

export function ExpensesPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();

  const filterSearch = searchParams.get("search") ?? "";
  const filterCurrency = searchParams.get("currency") ?? "";
  const filterDateFrom = searchParams.get("dateFrom") ?? "";
  const filterDateTo = searchParams.get("dateTo") ?? "";

  const [searchInput, setSearchInput] = useState(filterSearch);
  // Confirm-deletion dialog state. Keeping the target expense id here
  // (rather than passing through the row action closure) lets us reuse a
  // single ConfirmationDialog instance instead of rendering one per row.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  useEffect(() => {
    setSearchInput(filterSearch);
  }, [filterSearch]);

  const setFilter = useCallback(
    (
      key: "search" | "currency" | "dateFrom" | "dateTo",
      value: string,
    ): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          next.set("page", "1");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== filterSearch) setFilter("search", trimmed);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput, filterSearch, setFilter]);

  const listParams = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
      ...(filterSearch.trim() && { search: filterSearch.trim() }),
      ...(filterCurrency && { currency: filterCurrency as "UYU" | "USD" }),
      ...(filterDateFrom && { dateFrom: filterDateFrom }),
      ...(filterDateTo && { dateTo: filterDateTo }),
    }),
    [pagination.page, pagination.limit, filterSearch, filterCurrency, filterDateFrom, filterDateTo],
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: expenseKeys.list(listParams),
    queryFn: () => listExpenses(listParams),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (data != null) pagination.setTotal(data.total);
    else if (isError) pagination.setTotal(0);
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(
        t(
          "expenses.list.error",
          "No pudimos cargar los gastos. Intentá de nuevo en un momento.",
        ),
      );
    }
  }, [isError, t]);

  const deleteMutation = useMutation({
    mutationFn: deleteExpense,
    onSuccess: () => {
      toast.success(t("expenses.list.deleted", "Gasto eliminado"));
      void queryClient.invalidateQueries({ queryKey: expenseKeys.all });
    },
    onError: () => {
      toast.error(
        t("expenses.list.deleteError", "No pudimos eliminar el gasto"),
      );
    },
  });

  const expenses = data?.data ?? [];
  const hasFilters = Boolean(
    filterSearch.trim() || filterCurrency || filterDateFrom || filterDateTo,
  );

  const rowActions = (e: Expense): TableAction[] => [
    {
      label: t("expenses.list.delete", "Eliminar"),
      icon: Trash2,
      variant: "destructive",
      onClick: () => setPendingDeleteId(e.id),
    },
  ];

  const handleConfirmDelete = (): void => {
    if (!pendingDeleteId) return;
    deleteMutation.mutate(pendingDeleteId);
    setPendingDeleteId(null);
  };

  return (
    <div className="fx-content space-y-4">
      <PageHeader
        eyebrow={t("expenses.list.eyebrow", "Tus comprobantes de compra")}
        title={t("expenses.list.title", "Gastos")}
        description={t(
          "expenses.list.description",
          "Cargá tickets y facturas que no llegan por DGI para reconciliar tu IVA compras.",
        )}
        actions={
          <Button asChild>
            <Link to="/expenses/new">
              {t("expenses.list.add", "Nuevo gasto")}
            </Link>
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 sm:max-w-[260px] sm:flex-none">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t(
              "expenses.list.searchPlaceholder",
              "Buscar por comercio o RUT…",
            )}
            className="pl-9"
            aria-label={t("expenses.list.searchAria", "Buscar gastos")}
          />
        </div>

        <DatePickerField
          value={filterDateFrom}
          onChange={(v) => setFilter("dateFrom", v)}
          label={t("expenses.list.dateFrom", "Desde")}
          labelPosition="inline"
          triggerClassName="w-[150px]"
        />
        <DatePickerField
          value={filterDateTo}
          onChange={(v) => setFilter("dateTo", v)}
          label={t("expenses.list.dateTo", "Hasta")}
          labelPosition="inline"
          triggerClassName="w-[150px]"
        />

        <Select
          value={filterCurrency || "all"}
          onValueChange={(v) => setFilter("currency", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("expenses.list.currencyAll", "Todas")}
            </SelectItem>
            <SelectItem value="UYU">UYU</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
          </SelectContent>
        </Select>

        {hasFilters ? (
          <Button
            variant="ghost"
            onClick={() => {
              setSearchParams(new URLSearchParams(), { replace: true });
              setSearchInput("");
            }}
          >
            {t("expenses.list.clearFilters", "Limpiar filtros")}
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <TableCard>
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        </TableCard>
      ) : expenses.length === 0 ? (
        <TableCard>
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--fx-paper-2)] text-[var(--fx-ink-700)] dark:bg-[var(--dk-surface2)]">
              <Receipt className="h-6 w-6" aria-hidden />
            </div>
            <EmptyState
              className="!py-0"
              title={
                hasFilters
                  ? t("expenses.list.emptyFiltered", "Sin resultados")
                  : t("expenses.list.empty", "Aún no cargaste gastos")
              }
              message={
                hasFilters
                  ? t(
                      "expenses.list.emptyFilteredDescription",
                      "Probá con otros filtros o ampliá el período.",
                    )
                  : t(
                      "expenses.list.emptyDescription",
                      "Cargá tickets y facturas que no llegan por DGI para reconciliar tu IVA compras. Cada gasto que cargás reduce el IVA a pagar.",
                    )
              }
              action={
                hasFilters ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSearchParams(new URLSearchParams(), { replace: true });
                      setSearchInput("");
                    }}
                  >
                    {t("expenses.list.clearFilters", "Limpiar filtros")}
                  </Button>
                ) : (
                  <Button asChild>
                    <Link to="/expenses/new">
                      {t("expenses.list.add", "Cargar primer gasto")}
                    </Link>
                  </Button>
                )
              }
            />
          </div>
        </TableCard>
      ) : (
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("expenses.list.col.date", "Fecha")}</TableHead>
                <TableHead>{t("expenses.list.col.vendor", "Comercio")}</TableHead>
                <TableHead>{t("expenses.list.col.category", "Categoría")}</TableHead>
                <TableHead className="text-right">
                  {t("expenses.list.col.iva", "IVA")}
                </TableHead>
                <TableHead className="text-right">
                  {t("expenses.list.col.total", "Total")}
                </TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{formatDisplayDate(e.occurredAt)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{e.vendor ?? "—"}</div>
                    {e.vendorRut ? (
                      <div className="text-xs text-muted-foreground tabular-nums">
                        RUT {e.vendorRut}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>{e.category ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {e.iva ? formatMoney(e.iva, e.currency) : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatMoney(e.total, e.currency)}
                  </TableCell>
                  <TableCell>
                    <TableActionsMenu actions={rowActions(e)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            pagination={pagination}
            rowsPerPageLabel={t("expenses.list.rowsPerPage", "Por página")}
            pageOfLabel={t(
              "expenses.list.pageOf",
              "{{from}}-{{to}} de {{total}}",
              {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total,
              },
            )}
            onRefresh={() => void refetch()}
          />
        </TableCard>
      )}

      <ConfirmationDialog
        open={pendingDeleteId != null}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteId(null);
        }}
        title={t("expenses.list.confirmDeleteTitle", "Eliminar gasto")}
        description={t(
          "expenses.list.confirmDelete",
          "¿Eliminar este gasto? La acción no se puede deshacer.",
        )}
        confirmText={t("expenses.list.delete", "Eliminar")}
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
